import { z } from 'zod'

import { type Clinic, clinicIdSchema } from '../../entities/clinic'
import type { Member } from '../../entities/member'
import type { Patient } from '../../entities/patient'
import type { StaffMember } from '../../entities/staff'
import type { PatientHistoryContext } from '../patient-history'

export const writtenPrescriptionAuthorizationTypeSchema = z.enum(['none', 'wet', 'electronic'])

export const writtenPrescriptionAuthorizationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('none') }),
  z.object({ type: z.literal('wet'), vetId: z.uuid() }),
  z.object({ type: z.literal('electronic'), vetId: z.uuid() }),
])

export type WrittenPrescriptionAuthorizationType = z.infer<
  typeof writtenPrescriptionAuthorizationTypeSchema
>

export const writtenPrescriptionInputSchema = z.object({
  treatmentId: z.uuid(),
  clinicId: clinicIdSchema,
  authorization: writtenPrescriptionAuthorizationSchema,
})
export type WrittenPrescriptionInput = z.infer<typeof writtenPrescriptionInputSchema>

export type WrittenPrescriptionContext = {
  clinic: Pick<Clinic, 'id' | 'name' | 'address' | 'phone' | 'region'>
  patient: Pick<
    Patient,
    'id' | 'name' | 'species' | 'breed' | 'sex' | 'dateOfBirth' | 'microchipNumber'
  >
  owner: Pick<
    Member,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'phone'
    | 'address1'
    | 'address2'
    | 'city'
    | 'postalCode'
    | 'region'
  >
  prescription: {
    productName: string
    formulation: string | null
    quantity: number
    unit: string | null
    directions: string | null
    veterinaryCascade: boolean
    validUntil: string | null
    repeatsAllowed: number | null
    issuedAt: string
  }
  authorizingVet:
    | (Pick<StaffMember, 'id' | 'firstName' | 'lastName' | 'licenseNumber' | 'regions'> & {
        signatureDataUrl: string | null
      })
    | null
}

export const vaccinationRecordInputSchema = z.object({ patientId: z.uuid() })
export type VaccinationRecordInput = z.infer<typeof vaccinationRecordInputSchema>

export type VaccinationRecordContext = {
  clinic: Pick<Clinic, 'id' | 'name' | 'address' | 'phone' | 'region'>
  patient: Pick<Patient, 'id' | 'name' | 'species' | 'breed' | 'dateOfBirth' | 'microchipNumber'>
  owner: Pick<
    Member,
    'firstName' | 'lastName' | 'address1' | 'address2' | 'city' | 'postalCode' | 'region'
  >
  /** Newest first. */
  vaccinations: {
    id: string
    name: string
    givenAt: string
    /** Clinic the shot was given at, which fixes the day it was given on; null for historical records. */
    clinicId: string | null
    batchNumbers: string[]
    /** Uncancelled reminders, including those a later shot superseded, soonest first. */
    reminders: { target: string; dueAt: string }[]
  }[]
}

export const patientHistoryInputSchema = z.object({ patientId: z.uuid() })
export type PatientHistoryInput = z.infer<typeof patientHistoryInputSchema>

export const vaccinationCertificateInputSchema = z.object({
  treatmentId: z.uuid(),
  vetId: z.uuid(),
})
export type VaccinationCertificateInput = z.infer<typeof vaccinationCertificateInputSchema>

type CertificateVet = Pick<StaffMember, 'firstName' | 'lastName' | 'licenseNumber' | 'regions'>

type CertificatePatient = Pick<
  Patient,
  'name' | 'species' | 'breed' | 'color' | 'sex' | 'isNeutered' | 'dateOfBirth' | 'microchipNumber'
>

type CertificateOwner = Pick<
  Member,
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'postalCode'
  | 'region'
>

export type StandardVaccinationCertificateContext = {
  kind: 'standard'
  clinic: Pick<Clinic, 'id' | 'name' | 'address' | 'phone' | 'region'>
  patient: CertificatePatient
  owner: CertificateOwner
  vet: CertificateVet
  issuedAt: string
  vaccination: {
    name: string
    givenAt: string
    /** Clinic the shot was given at, which fixes the day it was given on. */
    clinicId: string | null
    /** Null when the stock has no batch number; printed as a blank to fill in. */
    batchNumber: string | null
    /** Uncancelled reminders, including those a later shot superseded, soonest first. */
    reminders: { target: string; dueAt: string }[]
  }
}

export type RabiesVaccinationCertificateContext = {
  kind: 'rabies'
  clinic: Pick<Clinic, 'id' | 'name' | 'address' | 'phone' | 'region'>
  patient: CertificatePatient
  owner: CertificateOwner
  /** Weight in kg at the shot: from its consultation, or else the last one recorded before it. */
  weight: string | null
  vet: CertificateVet
  issuedAt: string
  /** Details are null when not recorded; the certificate prints a blank for the vet to fill in. */
  vaccination: {
    name: string
    manufacturer: string | null
    batchNumber: string | null
    batchExpiry: string | null
    tagNumber: string | null
    givenAt: string
    clinicId: string | null
    nextDueAt: string | null
  }
}

/** US owners get the rabies certificate for a rabies shot; everything else gets the standard one. */
export type VaccinationCertificateContext =
  | StandardVaccinationCertificateContext
  | RabiesVaccinationCertificateContext

export const documentInputSchemas = {
  'written-prescription': writtenPrescriptionInputSchema,
  'vaccination-record': vaccinationRecordInputSchema,
  'patient-history': patientHistoryInputSchema,
  'vaccination-certificate': vaccinationCertificateInputSchema,
}

const documentInputsSchema = z.object(documentInputSchemas)

export const documentTypeSchema = documentInputsSchema.keyof()
export type DocumentType = z.infer<typeof documentTypeSchema>
export type DocumentInputs = z.infer<typeof documentInputsSchema>

export type DocumentContexts = {
  'written-prescription': WrittenPrescriptionContext
  'vaccination-record': VaccinationRecordContext
  'patient-history': PatientHistoryContext
  'vaccination-certificate': VaccinationCertificateContext
}

export const documentPreviewParams = z.object({ documentType: documentTypeSchema })

/**
 * Any document's input; the handler checks it against the one `documentType` names.
 * Loose so a branch matching first can't drop another document's fields before that check.
 */
export const postDocumentPreviewBodySchema = z.union([
  writtenPrescriptionInputSchema.loose(),
  vaccinationRecordInputSchema.loose(),
  patientHistoryInputSchema.loose(),
  vaccinationCertificateInputSchema.loose(),
])

export type DocumentPreviewInput = {
  [T in DocumentType]: { documentType: T; input: DocumentInputs[T] }
}[DocumentType]
